import { useRef, type HTMLAttributes, type ReactNode } from "react";
import { type CSSObject } from "@emotion/react";
import {
  constants,
  LoadingSpinner,
  Table,
  useWindowSize,
  visuallyHiddenStyles,
} from "@newsela/angelou";

import { type TableData } from "@/reports/teacher/utils/formatReadingActivityClassroomData";
import ResponseError from "@/errors/ResponseError";
import { DownloadCSVProps } from "@/reports/components/DownloadCSVButton";
import {
  FullWidthLoadingError,
  errorHeader,
  errorMessage,
} from "../FullWidthLoadingError";
import TableDescription from "./components/TableDescription";

import TableTitle from "./components/TableTitle";
import {
  emptyDataCss,
  filterContainer,
  tableContainerCss,
  tableCss,
  tableRootCss,
  tablePaginationCss,
  getColumnScaleWidthCSS,
} from "./ReportTable.style";

export interface TableRow {
  values: {
    [key: string]: string | number | ReactNode;
  };
}

export interface DataValue {
  title?: string;
  level?: string;
  grade?: string;
  name?: string;
  unique_id?: string;
}
export interface TableCell<T> {
  cell: {
    value: T;
    row: {
      index: number;
    };
  };
}
export interface FilterComponentProps {
  column: Record<string, unknown> & {
    filterValue: unknown;
    setFilter: (filterValue: unknown) => void;
  };
}
export type FilterComponent = (filterProps: FilterComponentProps) => ReactNode;

export interface CSVFiltersType {
  min_date: string;
  max_date: string;
  entity_id: string;
}

/**
 * to strongly type the array of object we first need to make a map where the key is the same
 * as the accessor.
 */
type WrapInObj<T> = {
  [key in keyof T]: {
    accessor: key;
    fixed?: boolean;
    Cell: (cell: TableCell<T[key]>) => ReactNode;
    Header: () => ReactNode;
    disableSortBy?: boolean;
    sortType?: (rowA: TableRow, rowB: TableRow, columnId: string) => number;
    filter?:
      | ((
          rows: Array<{ values: T }>,
          columnId: [key],
          filter: unknown,
        ) => Array<{ values: T }>)
      | null;
    Filter?: FilterComponent | null;

    /**
     * A positive integer representing the relative width of the column.
     * If not provided, the column defaults to 1 (equally fit content width).
     */
    scaleWidth?: number;
  };
};

/**
 * now that we have a map where the key matches the accessor, and everything in the
 * object is keyed off that accessor, we can "unwrap" it and put each strongly typed
 * object into an array
 */
type UnwrapToArray<T> = Array<WrapInObj<T>[keyof T]>;

export interface ReportTableProps<T = TableData> {
  columns: UnwrapToArray<T>;
  data: T[];
  initialState: {
    hiddenColumns?: string[];
    pageSize: number;
    totalResults: number;
    pageCount: number;
  };
  loading?: boolean | undefined;
  error?: typeof ResponseError | boolean | undefined;
  title: string;
  description?: string;
  onControlsUpdate: (reactTableProps: {
    filters: {
      id: keyof T;
      value: unknown;
    }[];
    pageSize: number;
    pageIndex: number;
    globalFilter?: unknown;
    sortBy: {
      desc: boolean;
      id: keyof T;
    }[];
  }) => void | undefined;
  __cssFor?: {
    root: CSSObject;
  };
  isFirstColumnFixed?: boolean;
  emptyDataMessage?: string;
  downloadCSVProps?: DownloadCSVProps | undefined;
}
const DEFAULT_EMPTY_DATA_MESSAGE =
  "No activity has occurred for selected filters";

const ReportTable = <T = TableData,>(props: ReportTableProps<T>) => {
  const {
    columns,
    data,
    initialState,
    loading,
    error,
    title,
    description,
    onControlsUpdate,
    __cssFor,
    isFirstColumnFixed = true,
    emptyDataMessage = DEFAULT_EMPTY_DATA_MESSAGE,
    downloadCSVProps,
  } = props;

  const emptyData = !loading && !error && !data.length;

  const handlePagination =
    loading || error || emptyData ? { __renderPagination: () => null } : {};

  const tableRef = useRef<null | HTMLDivElement>(null);

  const handleControlsUpdate = (passedProps: {
    filters: { id: keyof T; value: unknown }[];
    pageSize: number;
    pageIndex: number;
    globalFilter?: unknown;
    sortBy: { desc: boolean; id: keyof T }[];
  }) => {
    onControlsUpdate(passedProps);
    tableRef.current?.scrollIntoView();
  };

  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { xs },
    },
  } = constants;
  const isMobile = windowWidth < xs;

  const columnsWithFirstFixed = isFirstColumnFixed
    ? columns.map((col, index) => {
        if (index === 0) {
          return { ...col, fixed: !isMobile };
        }
        return col;
      })
    : columns;

  const DEFAULT_COLUMN_SCALE_WIDTH = 1;
  const columnScaleWidths = columns.map(({ scaleWidth }) =>
    scaleWidth && scaleWidth > 0 ? scaleWidth : DEFAULT_COLUMN_SCALE_WIDTH,
  );
  const loadingMessage = `Loading ${title} table data...`;

  return (
    <div ref={tableRef}>
      <Table
        __cssFor={{
          root: [tableRootCss, __cssFor?.root],
          tableContainer: tableContainerCss,
          table: [tableCss, [getColumnScaleWidthCSS(columnScaleWidths)]],
          TablePagination: tablePaginationCss,
        }}
        columns={columnsWithFirstFixed}
        __renderTitle={(
          passedProps: HTMLAttributes<HTMLHeadingElement> & { css?: CSSObject },
        ) => (
          <TableTitle
            {...passedProps}
            title={title}
            downloadCSVProps={downloadCSVProps}
          />
        )}
        __renderTableDescription={(
          passedProps: HTMLAttributes<HTMLHeadingElement> & { css?: CSSObject },
        ) => <TableDescription {...passedProps} description={description} />}
        __renderColumnFilters={(filters: ReactNode[]) => {
          return <div css={filterContainer}>{filters}</div>;
        }}
        data={data}
        initialState={{
          ...initialState,
        }}
        useManualControls
        onControlsUpdate={handleControlsUpdate}
        pageCount={initialState.pageCount}
        totalItems={initialState.totalResults}
        autoResetFilters={false}
        paginationOptions={[
          {
            value: 25,
            label: "25",
          },
          {
            value: 50,
            label: "50",
          },
          {
            value: 75,
            label: "75",
          },
        ]}
        {...handlePagination}
      />
      <>
        {loading && <LoadingSpinner title={loadingMessage} />}
        <div
          css={visuallyHiddenStyles}
          role="status"
          aria-label={loadingMessage}
        >
          {loading ? loadingMessage : ""}
        </div>
      </>
      {error ? (
        <FullWidthLoadingError header={errorHeader} message={errorMessage} />
      ) : emptyData ? (
        <div css={emptyDataCss}>{emptyDataMessage}</div>
      ) : null}
    </div>
  );
};

export default ReportTable;
